import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon, Button, Tag, Input } from '../../../pages/DesignSystem';
import stripeService from '../../../supabase/stripe.service';
import { SUBSCRIPTION_PLANS, formatPriceWithInterval } from '../../../types/subscription.types';
import { supabase } from '../../../config/supabase';
import './PublishLimitModal.css';

interface PublishLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  organizerId: string;
  eventsUsed: number;
  eventsLimit: number;
}

/**
 * PublishLimitModal - Shows when user has reached their event limit
 * Offers 3 options: Pro Monthly, Pro Yearly, or Pay-per-event
 */
const PublishLimitModal: React.FC<PublishLimitModalProps> = ({
  isOpen,
  onClose,
  eventId,
  organizerId,
  eventsUsed,
  eventsLimit
}) => {
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Referral system states
  const [referrerEmail, setReferrerEmail] = useState('');
  const [referrerValid, setReferrerValid] = useState<boolean | null>(null);
  const [referrerName, setReferrerName] = useState('');
  const [checkingReferrer, setCheckingReferrer] = useState(false);

  // Get the plan objects (not pay_per_event for now, we'll handle it separately)
  const proMonthlyPlan = SUBSCRIPTION_PLANS.find(p => p.id === 'monthly')!;
  const proYearlyPlan = SUBSCRIPTION_PLANS.find(p => p.id === 'yearly')!;
  const payPerEventPlan = SUBSCRIPTION_PLANS.find(p => p.id === 'pay_per_event')!;

  // Debounced function to check referrer validity via secure RPC
  const checkReferrer = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout | null = null;

      return (email: string) => {
        if (timeoutId) clearTimeout(timeoutId);

        if (!email || !email.includes('@')) {
          setReferrerValid(null);
          setReferrerName('');
          setCheckingReferrer(false);
          return;
        }

        setCheckingReferrer(true);

        timeoutId = setTimeout(async () => {
          try {
            // Use secure RPC to validate referrer without exposing sensitive data
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error } = await (supabase.rpc as any)('validate_referrer_code', {
              referrer_email: email
            });

            if (error) {
              console.error('Error checking referrer:', error);
              setReferrerValid(false);
              setReferrerName('Error validating referrer');
              return;
            }

            const result = Array.isArray(data) ? data[0] : data;

            if (!result) {
              setReferrerValid(false);
              setReferrerName('Error validating referrer');
            } else if (result.is_valid) {
              setReferrerValid(true);
              setReferrerName(result.organizer_name || email);
            } else {
              setReferrerValid(false);
              // Map error reasons to user-friendly messages
              switch (result.error_reason) {
                case 'not_found':
                  setReferrerName('User not found');
                  break;
                case 'self_referral':
                  setReferrerName('You cannot refer yourself');
                  break;
                case 'not_paid_subscriber':
                  setReferrerName('This user must have an active paid subscription');
                  break;
                default:
                  setReferrerName('Invalid referrer email');
              }
            }
          } catch (err) {
            console.error('Error checking referrer:', err);
            setReferrerValid(false);
            setReferrerName('Error validating referrer');
          } finally {
            setCheckingReferrer(false);
          }
        }, 500);
      };
    })(),
    []
  );

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSelectPlan = async (planId: 'monthly' | 'yearly' | 'pay_per_event') => {
    // Validate referrer if email is provided
    if (referrerEmail && referrerValid !== true) {
      setError('Please enter a valid referrer email or leave it empty');
      return;
    }

    setLoadingPlanId(planId);
    setError(null);

    try {
      // For pay-per-event, pass the eventId to auto-publish after payment
      const eventIdParam = planId === 'pay_per_event' ? eventId : undefined;

      // Pass referrerEmail only for subscriptions (monthly/yearly), not for pay-per-event
      const referrerEmailParam = (planId !== 'pay_per_event' && referrerEmail && referrerValid)
        ? referrerEmail.trim()
        : undefined;

      await stripeService.redirectToCheckout(
        planId,
        organizerId,
        eventIdParam,
        referrerEmailParam
      );
    } catch (err) {
      console.error('Checkout error:', err);
      setError((err as Error).message || 'Failed to start checkout');
      setLoadingPlanId(null);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="publish-limit-modal__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          <motion.div
            className="publish-limit-modal__dialog"
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="publish-limit-modal__header">
              <Icon name="Rocket" size={24} className="publish-limit-modal__icon publish-limit-modal__icon--upgrade" />
              <div className="publish-limit-modal__header-content">
                <h2 className="publish-limit-modal__title">Time to go unlimited!</h2>
                <p className="publish-limit-modal__subtitle">
                  You've used {eventsUsed}/{eventsLimit} free events — upgrade to keep publishing
                </p>
              </div>
            </div>

            {/* Trial Banner */}
            <motion.div
              className="publish-limit-modal__trial-banner"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.3 }}
            >
              <Icon name="Gift" size={20} />
              <div className="publish-limit-modal__trial-banner-text">
                <strong>First month FREE</strong>
                <span>Try Pro risk-free — cancel anytime, no charge</span>
              </div>
            </motion.div>

            <p className="publish-limit-modal__intro">
              Start free, or pay <strong>3€</strong> for a single event:
            </p>

            {/* Referral Input Section */}
            <div className="publish-limit-modal__referral-section">
              <Input
                id="referrer-email"
                name="referrerEmail"
                label="Referred by (optional)"
                placeholder="friend@example.com"
                value={referrerEmail}
                onChange={(val) => {
                  setReferrerEmail(val);
                  checkReferrer(val);
                }}
                disabled={loadingPlanId !== null}
                type="email"
              />

              {checkingReferrer && (
                <span className="publish-limit-modal__referral-checking">
                  <Icon name="Loader" size={14} />
                  Checking...
                </span>
              )}

              {referrerValid === true && (
                <div className="publish-limit-modal__referral-success">
                  <Icon name="Check" size={16} />
                  {referrerName} will get a free month thanks to you!
                </div>
              )}

              {referrerValid === false && referrerEmail && (
                <div className="publish-limit-modal__referral-error">
                  <Icon name="AlertCircle" size={16} />
                  {referrerName || 'Invalid referrer email'}
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="publish-limit-modal__error">
                <Icon name="AlertCircle" size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Plans Grid */}
            <div className="publish-limit-modal__plans-grid">
              {/* Pro Monthly */}
              <div className="publish-limit-modal__plan-card">
                <div className="publish-limit-modal__plan-header">
                  <Tag
                    label={proMonthlyPlan.name}
                    type="status"
                    status="published"
                    size="sm"
                  />
                  <div className="publish-limit-modal__plan-price-wrapper">
                    <span className="publish-limit-modal__plan-price--free">$0 today</span>
                    <span className="publish-limit-modal__plan-price--after">
                      then {formatPriceWithInterval(proMonthlyPlan)}
                    </span>
                  </div>
                </div>
                <ul className="publish-limit-modal__plan-features">
                  <li>
                    <Icon name="Check" size={14} />
                    <span>Unlimited events</span>
                  </li>
                  <li>
                    <Icon name="Check" size={14} />
                    <span>Full analytics</span>
                  </li>
                  <li>
                    <Icon name="Gift" size={14} />
                    <span className="publish-limit-modal__feature--highlight">30 days free trial</span>
                  </li>
                </ul>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleSelectPlan('monthly')}
                  disabled={loadingPlanId !== null}
                  className="publish-limit-modal__plan-button"
                >
                  {loadingPlanId === 'monthly' ? 'Loading...' : 'Start Free Trial'}
                </Button>
              </div>

              {/* Pro Yearly (Recommended) */}
              <div className="publish-limit-modal__plan-card publish-limit-modal__plan-card--recommended">
                <div className="publish-limit-modal__recommended-badge">Best Value</div>
                <div className="publish-limit-modal__plan-header">
                  <Tag
                    label={proYearlyPlan.name}
                    type="status"
                    status="published"
                    size="sm"
                  />
                  <div className="publish-limit-modal__plan-price-wrapper">
                    <span className="publish-limit-modal__plan-price--free">$0 today</span>
                    <span className="publish-limit-modal__plan-price--after">
                      then {formatPriceWithInterval(proYearlyPlan)}
                    </span>
                  </div>
                  <div className="publish-limit-modal__savings-badge">
                    4 months free
                  </div>
                </div>
                <ul className="publish-limit-modal__plan-features">
                  <li>
                    <Icon name="Check" size={14} />
                    <span>Unlimited events</span>
                  </li>
                  <li>
                    <Icon name="Check" size={14} />
                    <span>Full analytics</span>
                  </li>
                  <li>
                    <Icon name="Gift" size={14} />
                    <span className="publish-limit-modal__feature--highlight">30 days free trial</span>
                  </li>
                </ul>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleSelectPlan('yearly')}
                  disabled={loadingPlanId !== null}
                  className="publish-limit-modal__plan-button"
                >
                  {loadingPlanId === 'yearly' ? 'Loading...' : 'Start Free Trial'}
                </Button>
              </div>

              {/* Pay per Event */}
              <div className="publish-limit-modal__plan-card">
                <div className="publish-limit-modal__plan-header">
                  <Tag
                    label={payPerEventPlan.name}
                    type="status"
                    status="draft"
                    size="sm"
                  />
                  <div className="publish-limit-modal__plan-price">
                    {formatPriceWithInterval(payPerEventPlan)}
                  </div>
                </div>
                <ul className="publish-limit-modal__plan-features">
                  <li>
                    <Icon name="Check" size={14} />
                    <span>Publish this event</span>
                  </li>
                  <li>
                    <Icon name="Check" size={14} />
                    <span>No commitment</span>
                  </li>
                </ul>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleSelectPlan('pay_per_event')}
                  disabled={loadingPlanId !== null}
                  className="publish-limit-modal__plan-button"
                >
                  {loadingPlanId === 'pay_per_event' ? 'Loading...' : 'Pay & Publish'}
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="publish-limit-modal__actions">
              <Button variant="ghost" onClick={onClose} disabled={loadingPlanId !== null}>
                Cancel
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PublishLimitModal;
